import React, { useMemo } from "react";

import TableContainer from "@mui/material/TableContainer";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import { styled, useTheme } from "@mui/material/styles";
import { Caption, Body1 } from "../index";

export interface Column<T> {
  columnName: string;
  value: (arg: T) => React.ReactNode;
  fillPanel?: boolean /* If true then the first cell of this column will fill the entire panel */;
}

interface CohereTableWithPanelsProps<T> {
  /* displays panels with multiple rows in each if
    supplied a 2D array. displays one row per panel if given
    a flat array. */
  panels: T[][] | T[];
  columns: Column<T>[];
}

export default function CohereTableWithPanels<T>({ panels, columns }: CohereTableWithPanelsProps<T>) {
  return (
    <TableContainer>
      <StyledTable>
        <TableHead>
          <TableRow>
            {columns.map((column, idx) => (
              <StyledTableCellHead key={`${column.columnName}_${idx}`}>
                <Caption color="textSecondary">{column.columnName}</Caption>
              </StyledTableCellHead>
            ))}
          </TableRow>
        </TableHead>
        {panels.map((panel, panel_idx) => (
          <TableBody key={`panel_${panel_idx}`}>
            {isMultiRowPanel(panel) ? (
              <>
                {panel.map((row, row_idx) => (
                  <PanelRow
                    key={`row_${row_idx}`}
                    rowData={row}
                    columns={columns}
                    row_idx={row_idx}
                    numRows={panel.length}
                  />
                ))}
              </>
            ) : (
              <PanelRow rowData={panel} columns={columns} row_idx={0} numRows={1} />
            )}
          </TableBody>
        ))}
      </StyledTable>
    </TableContainer>
  );
}

// bitmasks for cell position within a panel
const TOP = 1 << 3;
const BOTTOM = 1 << 2;
const LEFT = 1 << 1;
const RIGHT = 1 << 0;

interface PanelRowProps<T> {
  rowData: T;
  columns: Column<T>[];
  row_idx: number;
  numRows: number;
}

function PanelRow<T>({ rowData, columns, row_idx, numRows }: PanelRowProps<T>) {
  return (
    <TableRow>
      {columns.map((column, col_idx) => {
        let position = 0;
        if (row_idx === 0) {
          position |= TOP;
        }
        if (row_idx === numRows - 1 || column.fillPanel) {
          position |= BOTTOM;
        }
        if (col_idx === 0) {
          position |= LEFT;
        }
        if (col_idx === columns.length - 1) {
          position |= RIGHT;
        }
        return (
          <PanelCell
            key={`row_${row_idx}_column_${col_idx}`}
            column={column}
            position={position}
            rowData={rowData}
            numRows={numRows}
          />
        );
      })}
    </TableRow>
  );
}

interface PanelCellProps<T> {
  column: Column<T>;
  position: number;
  rowData: T;
  numRows: number;
}

function PanelCell<T>({ column, position, rowData, numRows }: PanelCellProps<T>) {
  const cellValue = useMemo(() => column.value(rowData), [rowData, column]);
  const borderStyles = usePanelTableCellBorderStyles(position);

  if (column.fillPanel && !cellValue) {
    return null;
  }

  return (
    <PanelTableCell rowSpan={column.fillPanel ? numRows : undefined} style={borderStyles}>
      <Body1 style={{ fontSize: 13, lineHeight: "15px" }} component="span">
        {cellValue}
      </Body1>
    </PanelTableCell>
  );
}

function usePanelTableCellBorderStyles(position: number) {
  const theme = useTheme();

  return {
    borderTop: position & TOP ? `1px solid ${theme.palette.divider}` : "none",
    borderBottom: position & BOTTOM ? `1px solid ${theme.palette.divider}` : "none",
    borderLeft: position & LEFT ? `1px solid ${theme.palette.divider}` : "none",
    borderRight: position & RIGHT ? `1px solid ${theme.palette.divider}` : "none",
    paddingTop: position & TOP ? theme.spacing(1) : undefined,
    paddingBottom: position & BOTTOM ? theme.spacing(1) : undefined,
    paddingLeft: position & LEFT ? theme.spacing(1) : undefined,
    paddingRight: position & RIGHT ? theme.spacing(1) : undefined,
    borderTopLeftRadius: position & TOP && position & LEFT ? 4 : undefined,
    borderTopRightRadius: position & TOP && position & RIGHT ? 4 : undefined,
    borderBottomLeftRadius: position & BOTTOM && position & LEFT ? 4 : undefined,
    borderBottomRightRadius: position & BOTTOM && position & RIGHT ? 4 : undefined,
  };
}

function isMultiRowPanel<T>(panel: T[] | T): panel is T[] {
  return Array.isArray(panel);
}

const StyledTableCellHead = styled(TableCell)(({ theme }) => ({
  padding: theme.spacing(1, 1.5),
  whiteSpace: "nowrap",
  "&:first-child": {
    paddingLeft: theme.spacing(1),
  },
  "&.MuiTableCell-head": {
    lineHeight: "1rem",
  },
  border: "none",
}));

const StyledTable = styled(Table)(({ theme }) => ({
  borderCollapse: "separate",
  "& .MuiTableBody-root:nth-last-child(n+2):after": {
    // prettier-ignore
    content: "''",
    display: "block",
    height: theme.spacing(1),
  },
}));

const PanelTableCell = styled(TableCell)(({ theme }) => ({
  // backgroundColor: theme.palette.table.panelBackground, this aint workin for some reason
  backgroundColor: "#F5F5F5",
  padding: theme.spacing(1.5),
  verticalAlign: "top",
}));
